<script setup>
import { ref, reactive, onBeforeMount } from 'vue';
import Footer from './Footer.vue';
import Header from './Header.vue';
import SEO from '@/components/SEO.vue';
import { i18n } from '@/i18n';
import { useRouter } from 'vue-router';
import CreateChat from '@/components/create/CreateChat.vue';
import { getUser } from '@/lib/user';
import { getProductScenes, products } from '@/lib/product';
import PresetCardPrompt from '@/components/create/PresetCardPrompt.vue';
import { getCurrentModel } from '@/lib/gen/prompt';
import BackgroundLayer from '@/components/BackgroundLayer.vue';
import ProductSelector from '@/components/create/ProductSelector.vue';

const router = useRouter();
const props = defineProps({
  lang: {
    type: String,
    default: '',
  },
});

const selectedScene = ref(products[0]);

onBeforeMount(() => {
  const user = getUser();
  if (user) {
    router.push('/create');
  }
  getProductScenes().then((list) => {
    chatState.scenes = list;
  });
});

const chatState = reactive({
  prompt: '',
  model: getCurrentModel(),
  scenes: [],
  count: 1,
  theme: null,
  image: null,
  assets: [],
  attach: false,
  attachActiveTab: '',
  documents: [],
});

function selectPrompt(prompt) {
  chatState.image = null;
  chatState.prompt = prompt;
}

const useCases = [
  {
    image:
      'https://static.seedeai.com/templates/e14b27c7-e74b-4ec2-80dd-604713dfcf28_thumbnail.webp?v=1',
  },
  {
    image:
      'https://static.seedeai.com/templates/7ae14d1f-1711-48bc-b359-ef3172325ad6_thumbnail.webp?v=1',
  },
  {
    templateId: 'd0f3afe5-9b1f-4bcf-92af-4968ce7f998b',
    image:
      'https://static.seedeai.com/templates/d0f3afe5-9b1f-4bcf-92af-4968ce7f998b_thumbnail.webp?v=2',
  },
  {
    templateId: '5ac5e1e2-860d-4418-aead-d3cbd6650727',
    title: "Jobs' Letter to Innovators",
    image:
      'https://static.seedeai.com/templates/5ac5e1e2-860d-4418-aead-d3cbd6650727_thumbnail.webp?v=1',
  },

  {
    templateId: '0c872842-7a31-4487-8183-49dfbd6a78c2',
    title: 'Poster made with seede ai',
    image:
      'https://static.seedeai.com/templates/83951046-3b6b-41b7-9231-1ff3d1878591_thumbnail.webp?v=1',
  },
  {
    templateId: 'bc5bcc86-70da-45e1-8d89-b55eec2ae2cd',
    title: 'Poster made with seede ai',
    image:
      'https://static.seedeai.com/templates/bc5bcc86-70da-45e1-8d89-b55eec2ae2cd_thumbnail.webp?v=1',
  },
  {
    templateId: '053b7c69-2686-47f5-ad95-652e666509e1',
    title: 'Poster made with seede ai',
    image:
      'https://static.seedeai.com/templates/973118ed-5002-4244-9c8f-892eb3f56260_thumbnail.webp?v=1',
  },
  {
    image:
      'https://static.seedeai.com/templates/edb79c88-d7ef-492c-8ee8-c5372ab4a2ab_thumbnail.webp?v=1',
  },
  {
    image:
      'https://static.seedeai.com/templates/bd34cf68-e48a-4e13-832e-b8c12b25a52b_thumbnail.webp?v=1',
  },
  {
    image:
      'https://static.seedeai.com/templates/6c5cb0d2-ce3a-492c-a5bc-f12ac0baeaff_thumbnail.webp?v=1',
  },
  {
    image:
      'https://static.seedeai.com/templates/8a7cedff-e1db-4c33-bda8-db89389a1785_thumbnail.webp?v=1',
  },
  {
    image:
      'https://static.seedeai.com/templates/c701faa2-bb94-4239-975a-968508d6332b_thumbnail.webp?v=2',
  },
  {
    image:
      'https://static.seedeai.com/templates/1f74e2b5-ec6d-4bd5-8280-4c636903f9e9_thumbnail.webp?v=1',
  },

  {
    image:
      'https://static.seedeai.com/templates/7666d17c-b5ee-4fdd-8c6c-a814e2c7436e_thumbnail.webp?v=1',
  },
  {
    image:
      'https://static.seedeai.com/templates/b723ab27-1d6b-44d6-a444-f8fe4b675a3b_thumbnail.webp?v=1',
  },
  {
    image:
      'https://static.seedeai.com/templates/142ea5b1-b910-4043-beee-d0dcdb26fd55_thumbnail.webp?v=1',
  },

  {
    image:
      'https://static.seedeai.com/templates/a0ebf2a4-29f8-4bc4-ab3a-3e1674f4b7ec_thumbnail.webp?v=1',
  },
  {
    image:
      'https://static.seedeai.com/templates/f6d5a0e0-29b1-4e06-824a-2cebf5081a7a_thumbnail.webp?v=1',
  },
  {
    image:
      'https://static.seedeai.com/templates/db7869d9-1398-48da-9a65-92b135a3984d_thumbnail.webp?v=1',
  },
  {
    image:
      'https://static.seedeai.com/templates/b4659eea-5277-4cc9-9937-b4b4651a553a_thumbnail.webp?v=1',
  },

  {
    title: 'useCases.flashCard.title',
    description: 'useCases.flashCard.description',
    icon: '📇',
    gradient: 'from-black/90 to-black/80',
    image:
      'https://static.seedeai.com/templates/2afce34d-6c69-45aa-8148-0af15b5d32bc_thumbnail.webp?v=1',
  },
];

if (props.lang) {
  i18n.setLocale(props.lang);
}

const $t = (key, value, b) => i18n.get(key, value, b);
</script>
<template>
  <div class="min-h-screen pt-[60px] pb-4 relative flex flex-col">
    <!-- Background Gradient -->
    <BackgroundLayer />

    <!-- SEO Section -->
    <SEO
      :title="$t('landing.seoTitle')"
      :description="$t('landing.seoDescription')"
      :keywords="$t('landing.seoKeywords')"
    />

    <!-- Header Section -->
    <Header />

    <!-- Hero Section - 标题 -->
    <section class="flex flex-col items-center relative overflow-visible w-full z-10 pt-[5vh]">
      <div class="flex flex-col gap-3 items-center px-8 md:px-0">
        <h1
          class="text-center font-heading text-3xl font-bold sm:text-5xl sm:leading-tight max-w-5xl text-[#111]"
          v-html="$t('landing.heroTitle')"
        ></h1>
        <p class="text-center text-black/40 text-base sm:text-xl">
          {{ $t('landing.creatorCount') }}
        </p>
      </div>
    </section>

    <!-- Hero Section - ProductSelector -->
    <section class="flex flex-col items-center relative overflow-visible w-full z-10 mt-8">
      <div class="w-full max-w-4xl mx-auto px-4 sm:px-8">
        <ProductSelector
          :alpha="false"
          :scenes="chatState.scenes"
          :disable-navigation="true"
          v-model="selectedScene"
        />
      </div>
    </section>

    <!-- Hero Section - AI 输入框和底部 bubble 卡片 -->
    <section
      class="flex flex-col items-center relative overflow-visible w-full z-10 pb-24 sm:pb-32"
    >
      <div class="w-full max-w-4xl mx-auto px-4 sm:px-8 min-h-[500px]">
        <CreateChat
          v-model="chatState.prompt"
          v-model:image="chatState.image"
          v-model:model="chatState.model"
          v-model:scene="selectedScene"
          :chatState="chatState"
          :scenes="chatState.scenes"
          :simple="true"
          :product-selector="false"
        />

        <PresetCardPrompt :scene="selectedScene" @select="selectPrompt" />
      </div>
    </section>

    <!-- Templates Section -->
    <section
      class="w-full px-4 lg:px-8 xl:px-16 flex flex-col items-center gap-8 py-12 sm:py-4 sm:gap-10 relative z-10"
    >
      <div class="flex flex-col gap-4 text-center">
        <h2 class="font-heading text-4xl font-bold sm:text-5xl text-black/90">
          {{ $t('landing.creationFromCommunity') }}
        </h2>
      </div>

      <div class="flex w-full sm:w-full flex-col py-4 sm:py-4">
        <!-- Desktop view: 5 columns -->
        <div class="hidden md:block columns-5 gap-4 w-full relative">
          <router-link to="/templates" v-for="(useCase, index) in useCases" :key="index">
            <div
              class="group select-none rounded-[16px] overflow-hidden bg-white hover:shadow transition-shadow mb-4 break-inside-avoid"
            >
              <div class="overflow-hidden rounded-t-[16px]">
                <video
                  v-if="useCase.video"
                  :src="useCase.video"
                  class="w-full object-cover"
                  autoplay
                  loop
                  muted
                  playsinline
                  :poster="useCase.image"
                />
                <img
                  v-else
                  :src="useCase.image"
                  :alt="$t(useCase.title)"
                  class="w-full object-cover transform group-hover:scale-105 transition-transform duration-200"
                />
              </div>
            </div>
          </router-link>
        </div>

        <!-- Mobile view: 2 columns with fewer items -->
        <div class="md:hidden columns-3 gap-3 w-full relative">
          <router-link
            to="/templates"
            v-for="(useCase, index) in useCases.slice(0, 11)"
            :key="index"
          >
            <div
              class="group select-none rounded-[16px] overflow-hidden bg-white shadow-sm hover:shadow transition-shadow mb-3 break-inside-avoid"
            >
              <div class="overflow-hidden rounded-t-[16px]">
                <video
                  v-if="useCase.video"
                  :src="useCase.video"
                  class="w-full object-cover"
                  autoplay
                  loop
                  muted
                  playsinline
                  :poster="useCase.image"
                />
                <img
                  v-else
                  :src="useCase.image"
                  :alt="$t(useCase.title)"
                  class="w-full object-cover transform group-hover:scale-105 transition-transform duration-200"
                />
              </div>
            </div>
          </router-link>
        </div>

        <div class="flex justify-center mt-6 sm:mt-8">
          <router-link to="/templates">
            <button
              class="bg-white text-black px-8 py-3 rounded-lg text-base font-semibold border border-black/5 hover:bg-black/5 transition-all duration-200 ease-in-out"
            >
              View More
            </button>
          </router-link>
        </div>
      </div>
    </section>

    <div class="w-full border-t border-black/5 relative z-10"></div>

    <Footer class="mt-8 relative z-20" />
  </div>
</template>
